/**
 * THE PUBLIC DEMO'S INDUSTRIES (exception M1 in CLAUDE.md; Miles, 2026-10-30) — the addresses
 * prest0.ai/demo/<slug> answers, in the order they are built and offered.
 *
 * Until the site's own industry vocabulary lands (a parallel front-end session is building the industry pages), this is
 * the only list of these industries; when it lands, this module reads it instead of restating it (CLAUDE.md, one owner per
 * vocabulary). It is on the first download (the boot decides "demo or not" from it, and the site's header and footer
 * offer the demo from it), so it holds the ids, each one's name in the chooser and the entry's few words: nothing the
 * demo itself draws.
 */
export const DEMO_INDUSTRY_IDS = ['law', 'marketing', 'cpa-tax', 'real-estate', 'insurance', 'property-management', 'business'] as const;

export type DemoIndustryId = (typeof DEMO_INDUSTRY_IDS)[number];

export function isDemoIndustryId(value: string): value is DemoIndustryId {
  return (DEMO_INDUSTRY_IDS as readonly string[]).includes(value);
}

/**
 * Each industry's name where a visitor picks one (the chooser the site's header and footer open, in both designs). The
 * order is DEMO_INDUSTRY_IDS's. In the product's voice: lowercase as the header's actions are, an acronym as the site
 * writes one (ROI, FAQ).
 */
export const DEMO_INDUSTRY_LABELS: { readonly [K in DemoIndustryId]: string } = {
  law: 'law',
  marketing: 'marketing',
  'cpa-tax': 'CPA & tax',
  'real-estate': 'real estate brokerage',
  insurance: 'insurance',
  'property-management': 'property management',
  business: 'other businesses',
};

/**
 * The way inside prest0 from the public site, said the same in both designs: the action, and the chooser it opens.
 * "interactive demo" (Miles, 2026-11-02, for the menu, the day and the close), in place of "see what's inside" (Justin's,
 * the same day: "try to get away from the word demo"); the founders' order settles it (Miles first). The addresses stay
 * /demo/<industry>.
 */
export const DEMO_ENTRY_WORDS = {
  /** The header's, the home page's and the footer's action, and the chooser's title. */
  open: 'interactive demo',
  /** The chooser's sentence under its title. */
  lead: 'pick an industry to step inside prest0 as a made-up firm: its team, its threads, its work. no account needed.',
  /** The list's name, for a screen reader. */
  list: 'industries',
  /** The chooser's way out. */
  close: 'close',
} as const;
